import Big from 'big.js'
import getSymbolFromCurrency from 'currency-symbol-map'

import {getCurrency, isSupportedCountry, useCurrencySymbol} from '@/lib/locale/country'
import {getCurrencyFromLocale, isSupportedLocale} from '@/lib/locale/locale'
import formatNumber, {Format} from '@/utils/numbers/formatNumber'

interface PriceProps {
  price?: string | number | bigint | Big | undefined
  countryOrLocale?: string | null | undefined
  options?: Parameters<typeof formatNumber>[2]
}

export default memo(function Price({price, countryOrLocale, options}: PriceProps) {
  const currencySymbol = useCurrencySymbol()

  const derivedCurrencySymbol = useMemo(() => {
    try {
      if (typeof countryOrLocale === 'string') {
        if (isSupportedCountry(countryOrLocale)) {
          const currency = getCurrency(countryOrLocale)
          invariant(currency, 'Currency not found')
          const symbol = getSymbolFromCurrency(currency)
          invariant(symbol, 'Symbol not found')
          return symbol
        }

        if (isSupportedLocale(countryOrLocale)) {
          const currency = getCurrencyFromLocale(countryOrLocale)
          const symbol = getSymbolFromCurrency(currency)
          invariant(symbol, 'Symbol not found')
          return symbol
        }
      }
    } catch {
      // do nothing
    }
    return currencySymbol
  }, [countryOrLocale, currencySymbol])

  const numberNum = price && price instanceof Big ? price.toNumber() : (price ?? 0)
  let numberString = formatNumber(numberNum, Format.READABLE, options)
  let sign = ''
  if (numberString.startsWith('-')) {
    numberString = numberString.slice(1)
    if (numberNum !== 0) sign = '-' // prevent showing '-' for 0
  }

  return (
    <>
      {sign}
      {derivedCurrencySymbol}
      {numberString}
    </>
  )
})
